import React, { useContext, useState } from 'react';
import { ScrollView, TouchableOpacity, Platform } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { Appbar, IconButton, Button } from 'react-native-paper';
import {
  Box, VStack, HStack, Text, Center, Pressable,
} from 'native-base';
import * as ImagePicker from 'expo-image-picker';
import { useToast } from 'react-native-toast-notifications';
import TripPhotoGallery from '../../../components/trip/TripPhotoGallery';
import { useNavigation } from '@react-navigation/native';
import jwt_decode from 'jwt-decode';
import { TripsForEvent } from '../../../gql/trip/query';
import { ModalContext } from '../../../context/ModalContext';
import { SCREEN_NAMES } from '../../../utils/constant';
import ConfirmationModal from '../../../modals/Confirmation/ConfirmationModal';
import LoadingIndicator from '../../../components/commons/LoadingIndicator';
import { AuthContext } from '../../../context/AuthContext';
import { DELETE_TRIP, CREATE_RETURN_TRIP, ADD_TRIP_PHOTO, REMOVE_TRIP_PHOTO } from '../../../gql/trip/mutation';
import RefreshableScreen from '../../Refreshable/RefreshableScreen';
import ManageTripModal from '../../../modals/ManageTrip/ManageTrip';
import ManageSeatAssignmentModal from '../../../modals/ManageSeatAssignment/ManageSeatAssignmentModal';
import TransportationVisualizerWithAssignments from '../../../components/transportation/TransportationVisualizerWithAssignments';

const STATUS_COLORS = {
  SCHEDULED: 'blue.500',
  IN_PROGRESS: 'orange.500',
  COMPLETED: 'green.500',
  CANCELLED: 'red.500',
};

const STATUS_LABELS = {
  SCHEDULED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

function EventTripsTab({ event }) {
  const navigation = useNavigation();
  const toast = useToast();
  const { isSuperAdmin, authState } = useContext(AuthContext);
  const { data, loading, refetch } = useQuery(TripsForEvent, {
    variables: { eventId: Number(event.id) },
  });

  // Derive current user ID from JWT token
  const userId = authState?.accessToken
    ? jwt_decode(authState.accessToken).userId
    : null;
  const {
    toggleManageTripModal,
    displayManageTripModal,
    displayConfirmationModal,
    toggleConfirmationModal,
    toggleManageSeatAssignmentModal,
    displayManageSeatAssignmentModal,
  } = useContext(ModalContext);

  const handleTrackDriver = (trip) => {
    navigation.navigate(SCREEN_NAMES.TRIP_TRACKING, {
      tripId: trip.id,
      eventId: event.id,
    });
  };

  const [deleteTrip] = useMutation(DELETE_TRIP, {
    refetchQueries: [{ query: TripsForEvent, variables: { eventId: Number(event.id) } }],
  });

  const [createReturnTrip, { loading: creatingReturn }] = useMutation(CREATE_RETURN_TRIP, {
    refetchQueries: [{ query: TripsForEvent, variables: { eventId: Number(event.id) } }],
  });

  const [addTripPhoto] = useMutation(ADD_TRIP_PHOTO, {
    onCompleted: () => refetch(),
    onError: (error) => toast.show(error.message),
  });

  const [removeTripPhoto] = useMutation(REMOVE_TRIP_PHOTO, {
    onCompleted: () => refetch(),
    onError: (error) => toast.show(error.message),
  });

  const handlePhotoSlotPress = async (trip, phase, order) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.7,
      base64: true,
    });
    if (!result.canceled) {
      const asset = result.assets[0];
      const photo = Platform.OS === 'web'
        ? asset.uri
        : `data:${asset.mimeType};base64,${asset.base64}`;
      addTripPhoto({ variables: { tripId: Number(trip.id), phase, order, photo } });
    }
  };

  const [expandedTripId, setExpandedTripId] = useState(null);
  const [photoGalleryTripId, setPhotoGalleryTripId] = useState(null);

  const handleAdd = () => {
    toggleManageTripModal({
      title: 'Add Trip',
      trip: null,
      eventId: Number(event.id),
      eventLocation: event.location,
      onSave: refetch,
    });
  };

  const handleEdit = (trip) => {
    toggleManageTripModal({
      title: 'Edit Trip',
      trip,
      eventId: Number(event.id),
      eventLocation: event.location,
      onSave: refetch,
    });
  };

  const handleDelete = (trip) => {
    toggleConfirmationModal({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this trip?',
      handleSubmit: () => {
        deleteTrip({ variables: { id: trip.id } });
      },
    });
  };

  const handleManageSeats = (trip) => {
    toggleManageSeatAssignmentModal({
      trip,
      eventId: Number(event.id),
      onSave: refetch,
    });
  };

  const handleCreateReturnTrip = async (trip) => {
    try {
      await createReturnTrip({ variables: { originalTripId: trip.id } });
    } catch (error) {
      console.error('Error creating return trip:', error);
      // eslint-disable-next-line no-alert
      alert(`Erreur: ${error.message}`);
    }
  };

  const toggleExpand = (id) => {
    setExpandedTripId(expandedTripId === id ? null : id);
  };

  if (loading) {
    return <LoadingIndicator text="Chargement des transports..." />;
  }

  // Find the trips the current user is assigned to (as passenger)
  const myTrips = (data?.tripsForEvent || []).filter(
    (trip) => trip.seatAssignments?.some((sa) => sa.playerId === userId),
  );

  if (displayManageSeatAssignmentModal.display) {
    return <ManageSeatAssignmentModal />;
  }

  if (displayManageTripModal.display) {
    return <ManageTripModal eventId={event.id} />;
  }

  // Participant view: show only their assigned trips
  if (!isSuperAdmin) {
    return (
      <VStack flex={1} p={4} space={4}>
        <RefreshableScreen onRefresh={refetch}>
          <ScrollView flex={1}>
            <VStack space={3}>
              <Appbar.Header>
                <Appbar.Content title="Mon transport" />
              </Appbar.Header>

              {myTrips.length === 0 ? (
                <Center py={10}>
                  <Text fontSize="md" color="gray.500">Aucun trajet assigné</Text>
                </Center>
              ) : (
                myTrips.map((myTrip) => {
                  const mySeat = myTrip.seatAssignments?.find((sa) => sa.playerId === userId);
                  const isReturnTrip = myTrips.some((t) => t.linkedTripId === myTrip.id);
                  const label = isReturnTrip ? 'Trajet retour' : 'Trajet aller';
                  const isDriver = myTrip.driverId === userId;
                  return (
                    <Box key={myTrip.id} bg="white" p={4} borderRadius="md" shadow={1}>
                      <VStack space={3}>
                        <HStack alignItems="center" space={2}>
                          <Text fontWeight="bold" fontSize="lg" color="black">
                            {myTrip.transportation.name}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="white"
                            bg={STATUS_COLORS[myTrip.status]}
                            px={2}
                            py={0.5}
                            borderRadius="full"
                          >
                            {STATUS_LABELS[myTrip.status]}
                          </Text>
                          <Text fontSize="xs" color="blue.600" fontWeight="semibold">
                            {label}
                          </Text>
                        </HStack>

                        <Text fontSize="sm" color="gray.700">
                          {myTrip.driver
                            ? `Conducteur: ${myTrip.driver.firstName} ${myTrip.driver.lastName}`
                            : myTrip.guestDriverName
                              ? `Conducteur invité: ${myTrip.guestDriverName}`
                              : 'Conducteur: Compte supprimé'}
                        </Text>

                        <Text fontSize="sm" fontWeight="bold" color="blue.600">
                          {`Siège n°${mySeat.seatNumber}`}
                        </Text>

                        <Box bg="gray.50" p={3} borderRadius="md">
                          <VStack space={2}>
                            <Text fontSize="sm">
                              {`Départ: ${myTrip.departureLocation}`}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {new Date(myTrip.departureDateTime).toLocaleString()}
                            </Text>
                            <Text fontSize="sm">
                              {`Arrivée: ${myTrip.arrivalLocation}`}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {new Date(myTrip.arrivalDateTime).toLocaleString()}
                            </Text>
                          </VStack>
                        </Box>

                        {myTrip.distanceInKm && (
                          <Text fontSize="sm">
                            {`Distance: ${myTrip.distanceInKm.toFixed(1)} km`}
                          </Text>
                        )}
                        {myTrip.estimatedDuration && (
                          <Text fontSize="sm">
                            {`Durée estimée: ${Math.floor(myTrip.estimatedDuration / 60)}h${myTrip.estimatedDuration % 60}min`}
                          </Text>
                        )}

                        <TransportationVisualizerWithAssignments
                          type={myTrip.transportation.type}
                          seats={myTrip.transportation.seats}
                          color={myTrip.transportation.color}
                          seatAssignments={myTrip.seatAssignments}
                          onSeatPress={() => {}}
                          selectedSeat={mySeat.seatNumber}
                        />

                        {isDriver && (myTrip.status === 'SCHEDULED' || myTrip.status === 'IN_PROGRESS') && (
                          <Box bg="gray.50" p={3} borderRadius="md">
                            <TripPhotoGallery
                              photos={myTrip.photos || []}
                              editablePhase={myTrip.status === 'SCHEDULED' ? 'BEFORE' : 'AFTER'}
                              onSlotPress={(phase, order) => handlePhotoSlotPress(myTrip, phase, order)}
                              onRemovePhoto={(id) => removeTripPhoto({ variables: { id } })}
                            />
                          </Box>
                        )}

                        <Button
                          mode="contained"
                          icon="map-marker"
                          onPress={() => handleTrackDriver(myTrip)}
                          style={{ marginTop: 8 }}
                        >
                          Suivre le trajet
                        </Button>
                      </VStack>
                    </Box>
                  );
                })
              )}
            </VStack>
          </ScrollView>
        </RefreshableScreen>
      </VStack>
    );
  }

  // Admin view: full trip management
  return (
    <VStack flex={1} p={4} space={4}>
      {displayConfirmationModal.display && <ConfirmationModal />}
      <RefreshableScreen onRefresh={refetch}>
        <ScrollView flex={1}>
          <VStack space={3}>
            <Appbar.Header>
              <Appbar.Content title="Transports" />
              <Appbar.Action
                icon="plus-box"
                onPress={() => handleAdd()}
              />
            </Appbar.Header>

            {data?.tripsForEvent?.map((item) => (
              <Box
                key={item.id}
                bg="white"
                p={4}
                borderRadius="md"
                shadow={1}
              >
                <VStack space={2}>
                  <HStack justifyContent="space-between" alignItems="center">
                    <TouchableOpacity onPress={() => toggleExpand(item.id)}>
                      <VStack>
                        <HStack alignItems="center" space={2}>
                          <Text fontWeight="bold" fontSize="md" color="black">
                            {item.transportation.name}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="white"
                            bg={STATUS_COLORS[item.status]}
                            px={2}
                            py={0.5}
                            borderRadius="full"
                          >
                            {STATUS_LABELS[item.status]}
                          </Text>
                          {item.linkedTripId && (
                            <Text fontSize="xs" color="purple.600" fontWeight="semibold">
                              ↔ Trajet lié
                            </Text>
                          )}
                          <Pressable onPress={() => setPhotoGalleryTripId(photoGalleryTripId === item.id ? null : item.id)}>
                            <Text fontSize="xs" color="gray.600">
                              {`📷 ${(item.photos || []).length}/8`}
                            </Text>
                          </Pressable>
                        </HStack>
                        <HStack alignItems="center" space={1} mt={1}>
                          <Text fontSize="sm" color="gray.700">
                            {item.driver
                              ? `Conducteur: ${item.driver.firstName} ${item.driver.lastName}`
                              : item.guestDriverName
                                ? `Conducteur invité: ${item.guestDriverName}`
                                : 'Conducteur: Compte supprimé'}
                          </Text>
                          {item.driverMissingDocuments?.length > 0 && (
                            <Text fontSize="xs" color="orange.500">
                              {`⚠ Documents manquants : ${item.driverMissingDocuments.join(', ')}`}
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                    </TouchableOpacity>
                    <HStack space={1}>
                      <IconButton
                        icon="pencil"
                        size={24}
                        onPress={() => handleEdit(item)}
                      />
                      <IconButton
                        icon="trash-can"
                        size={24}
                        color="red"
                        onPress={() => handleDelete(item)}
                      />
                    </HStack>
                  </HStack>
                  {photoGalleryTripId === item.id && (
                    <Box mt={2} p={2} bg="gray.50" borderRadius="md">
                      <TripPhotoGallery photos={item.photos || []} readonly />
                    </Box>
                  )}
                  {expandedTripId === item.id && (
                    <VStack mt={2} space={2}>
                      <Text fontSize="sm">
                        {`Départ: ${item.departureLocation} à ${new Date(item.departureDateTime).toLocaleString()}`}
                      </Text>
                      <Text fontSize="sm">
                        {`Arrivée: ${item.arrivalLocation} à ${new Date(item.arrivalDateTime).toLocaleString()}`}
                      </Text>
                      <Text fontSize="sm">
                        {`Places: ${item.transportation.seats}`}
                      </Text>
                      {item.seatAssignments && item.seatAssignments.length > 0 && (
                        <Text fontSize="sm" color="green.600">
                          {`Sièges assignés: ${item.seatAssignments.length}/${item.transportation.seats}`}
                        </Text>
                      )}
                      {(() => {
                        const passengerSeats = (item.seatAssignments || []).filter(
                          (sa) => sa.seatNumber !== 1,
                        );
                        if (passengerSeats.length === 0) return null;
                        const paidCount = passengerSeats.filter((sa) => sa.cautionPaid).length;
                        const allPaid = paidCount === passengerSeats.length;
                        return (
                          <Text
                            fontSize="sm"
                            color={allPaid ? 'green.600' : 'orange.500'}
                            fontWeight="semibold"
                          >
                            {`Cautions: ${paidCount}/${passengerSeats.length}`}
                          </Text>
                        );
                      })()}
                      {item.distanceInKm && (
                        <Text fontSize="sm">
                          {`Distance: ${item.distanceInKm.toFixed(1)} km`}
                        </Text>
                      )}
                      {item.estimatedDuration && (
                        <Text fontSize="sm">
                          {`Durée estimée: ${Math.floor(item.estimatedDuration / 60)}h${item.estimatedDuration % 60}min`}
                        </Text>
                      )}
                      {item.linkedTrip && (
                        <Text fontSize="sm" color="purple.600">
                          {`↔ Lié à: ${item.linkedTrip.departureLocation} → ${item.linkedTrip.arrivalLocation}`}
                        </Text>
                      )}
                      <Button
                        mode="contained"
                        icon="seat"
                        onPress={() => handleManageSeats(item)}
                        style={{ marginTop: 8 }}
                      >
                        Gérer les sièges
                      </Button>
                      {!item.linkedTripId && (
                        <Button
                          mode="outlined"
                          icon="swap-horizontal"
                          onPress={() => handleCreateReturnTrip(item)}
                          loading={creatingReturn}
                          disabled={creatingReturn}
                          style={{ marginTop: 4 }}
                        >
                          Créer le trajet retour
                        </Button>
                      )}
                      {item.status === 'IN_PROGRESS' && (
                        <Button
                          mode="contained"
                          icon="map-marker"
                          onPress={() => handleTrackDriver(item)}
                          style={{ marginTop: 8 }}
                        >
                          Suivre le conducteur
                        </Button>
                      )}
                    </VStack>
                  )}
                </VStack>
              </Box>
            ))}
          </VStack>
        </ScrollView>
      </RefreshableScreen>
    </VStack>
  );
}

export default EventTripsTab;
